import React from "react";
import CIIOrbitDiagram from "./CIIOrbitDiagram";

const Lead = ({ children }) => <p className="wp-lead">{children}</p>;
const Pull = ({ children }) => <blockquote className="wp-pull">{children}</blockquote>;
const Bullets = ({ items }) => (
  <ul className="wp-bullets">{items.map((t) => <li key={t}>{t}</li>)}</ul>
);
const KV = ({ head, rows }) => (
  <table className="wp-table">
    <thead><tr><th>{head[0]}</th><th>{head[1]}</th></tr></thead>
    <tbody>{rows.map((r) => <tr key={r[0]}><td>{r[0]}</td><td>{r[1]}</td></tr>)}</tbody>
  </table>
);
const Sec = ({ eyebrow, title, children }) => (
  <section className="wp-sec">
    {eyebrow && <div className="wp-eyebrow">{eyebrow}</div>}
    <h2 className="wp-h2">{title}</h2>
    {children}
  </section>
);
const Stat = ({ big, sub, note }) => (
  <div className="wp-stat">
    <div className="wp-stat-big">{big}</div>
    <div className="wp-stat-sub">{sub}</div>
    {note && <div className="wp-stat-note">{note}</div>}
  </div>
);

export default function WhitePaper() {
  return (
    <div className="wp-page-bg">
      <div className="wp-actions no-print">
        <a href="/" className="wp-back" data-testid="wp-back">← Back to site</a>
        <button className="wp-print" onClick={() => window.print()} data-testid="wp-print">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>
          Print / Save as PDF
        </button>
      </div>

      <article className="wp-doc" data-testid="whitepaper">
        {/* ===== COVER ===== */}
        <header className="wp-cover">
          <img className="wp-logo" src="/brand/logo.png" alt="Energy Vault Group" />
          <div className="wp-kicker">ENERGY VAULT GROUP WHITEPAPER</div>
          <h1 className="wp-title">Born <span className="g">Intelligent</span></h1>
          <p className="wp-subtitle">Why Tomorrow&rsquo;s Buildings Must Not Be Built with Yesterday&rsquo;s Energy Methods</p>
          <p className="wp-cover-line">A new building should never need to be retrofitted with intelligence it could have been born with.</p>
          <div className="wp-cover-band">STOP WASTE BEFORE IT BEGINS</div>
          <div className="wp-cover-steps">DESIGN THE FOUNDATION&nbsp;&nbsp;•&nbsp;&nbsp;CONNECT THE INTELLIGENCE&nbsp;&nbsp;•&nbsp;&nbsp;PROTECT THE FUTURE</div>
          <div className="wp-cover-meta">For Council New Builds&nbsp;&nbsp;|&nbsp;&nbsp;Strategic discussion edition&nbsp;&nbsp;|&nbsp;&nbsp;2026</div>
        </header>

        <Sec eyebrow="BEFORE THE FIRST BRICK" title="A building is born once">
          <Lead>Long before the doors open, decisions are made that will shape how the building consumes energy, responds to change and protects money for decades.</Lead>
          <p>Drawings become specifications. Specifications become procurement packages. Equipment is selected, control philosophies are agreed and data responsibilities are divided. Once construction begins, those choices become progressively harder and more expensive to change.</p>
          <p>That makes a new build a rare opportunity: waste does not have to be discovered years later. It can be prevented before it is embedded.</p>
          <Pull>The most affordable retrofit is the one the building never needs.</Pull>
          <p>This paper is for the people who decide what a building will become: clients, architects, developers, engineers, contractors, quantity surveyors, procurement teams, operators and public-sector leaders.</p>
          <p>The first question is not what technology can be added later. It is what intelligence must exist from day one.</p>
        </Sec>

        <Sec eyebrow="THE DECISION THAT LASTS" title="Today&rsquo;s specification can govern decades of energy behaviour">
          <Lead>A building may look modern on opening day while its energy-management methods are already outdated.</Lead>
          <p>High-performance fabric and efficient equipment matter. But a building cannot protect performance it cannot see. If energy data remains fragmented, shallow or retrospective, future teams inherit an estate that may be efficient in theory yet difficult to understand in operation.</p>
          <Pull>Why are we building tomorrow&rsquo;s buildings with yesterday&rsquo;s energy methods?</Pull>
          <p>A design decision that omits granular visibility is not neutral. It passes uncertainty, investigation costs and retrofit disruption to the people who will operate the building later.</p>
          <p>And that is how waste can enter a building before the first occupant does.</p>
        </Sec>

        <Sec eyebrow="THE HIDDEN DESIGN DEFECT" title="Energy waste can be designed in before a building opens">
          <Lead>Waste is not created only by careless behaviour. It can be enabled by missing measurement, disconnected controls, weak commissioning and unclear ownership.</Lead>
          <p>It begins when a project:</p>
          <Bullets items={[
            "Measures only whole-building totals instead of significant assets and systems.",
            "Installs technologies that cannot exchange useful data.",
            "Treats monitoring as an optional operational extra.",
            "Completes commissioning without establishing a living performance baseline.",
            "Hands over equipment without handing over intelligible information.",
            "Leaves nobody clearly accountable for continuing optimisation.",
          ]} />
          <Pull>A building can meet a design standard and still begin life unable to explain its own energy behaviour.</Pull>
          <p>The danger is greatest when an old method is mistaken for a modern system.</p>
        </Sec>

        <Sec eyebrow="YESTERDAY&rsquo;S OLD ENERGY METHODS" title="A monthly total is not an operating strategy">
          <Lead>Traditional methods are built around meters, bills, periodic audits and disconnected building systems. They report consumption — but often fail to explain behaviour.</Lead>
          <KV head={["Yesterday's method", "What a new build requires"]} rows={[
            ["Monthly or quarterly totals", "Live, granular evidence"],
            ["Separate vendor systems", "Connected, interoperable architecture"],
            ["One-off commissioning snapshot", "A baseline that continues to learn"],
            ["Reactive fault investigation", "Earlier detection and proactive response"],
            ["Data trapped with specialists", "Role-based intelligence for every team"],
          ]} />
          <Pull>Do not build a new estate that must wait for the bill to discover what went wrong.</Pull>
          <p>This is not an argument against proven engineering. It is an argument for connecting engineering to the evidence needed to operate, verify and improve it.</p>
          <p>That same principle changes how we should design renewables and storage.</p>
        </Sec>

        <Sec eyebrow="GENERATION IS NOT VISIBILITY" title="Solar generates. Batteries store. Intelligence optimises.">
          <Lead>Solar PV, battery storage and electrification are important routes towards energy independence. But none can eliminate waste it cannot see.</Lead>
          <p>Without a digital foundation, valuable renewable generation may support loads that should never have existed. Battery capacity may be depleted by poor schedules, conflicting controls or hidden out-of-hours demand. The assets are working — but the building is not learning.</p>
          <Pull>A clean kilowatt-hour can still be wasted.</Pull>
          <p>An intelligent foundation allows the building to:</p>
          <Bullets items={[
            "Understand when and where demand is occurring.",
            "Coordinate consumption with solar generation and stored energy.",
            "Identify the loads eroding renewable and battery value.",
            "Verify whether optimisation is delivering the intended outcome.",
          ]} />
          <p>Whether solar PV and batteries are installed now or added later, the foundation must already be capable of connecting, monitoring and optimising them.</p>
        </Sec>

        <Sec eyebrow="THE DESIGN-STAGE OPPORTUNITY" title="Build visibility into the architecture — not onto it afterwards">
          <Lead>The right moment to decide what must be measured, connected and governed is while the building still exists on paper.</Lead>
          <p>The design brief should establish:</p>
          <Bullets items={[
            "Which systems, circuits and critical assets require granular monitoring.",
            "Which environmental and occupancy conditions provide operational context.",
            "How electricity, gas, water, heat, solar, storage and EV charging will connect.",
            "Who owns the information, its quality and the response to deviation.",
            "How data will remain accessible across the building's life.",
            "How future technologies can integrate without rebuilding the foundation.",
          ]} />
          <Pull>Digitalisation is not a finishing touch. It is part of the building&rsquo;s core infrastructure.</Pull>
        </Sec>

        <Sec eyebrow="MAKE THE INVISIBLE VISIBLE" title="Granular hardware gives the building its senses">
          <Lead>A building cannot think intelligently about conditions it cannot detect.</Lead>
          <p>Well-placed monitoring hardware captures the behaviour hidden beneath a whole-building meter: major plant, distribution circuits, temperature, humidity, air quality, occupancy, generation, storage and other operational conditions relevant to the estate.</p>
          <p>Granularity should be purposeful:</p>
          <Bullets items={[
            "Measure where a decision can be made.",
            "Capture context, not energy alone.",
            "Use open and documented communication routes.",
            "Design for safe access, maintenance and replacement.",
            "Avoid data collection with no defined operational owner.",
          ]} />
          <p>The objective is not to collect everything. It is to collect what the building needs to understand, explain and improve itself.</p>
        </Sec>

        <Sec eyebrow="COMMISSIONING WITH MEMORY" title="The baseline should begin before handover">
          <Lead>Commissioning proves that systems can operate. A living baseline shows how they continue to behave under real conditions.</Lead>
          <p>Instead of waiting months or years for enough bills to reveal a problem, the project can begin capturing performance during commissioning and seasonal operation. Schedules, setpoints, weather, occupancy and system interactions become part of the building&rsquo;s operational memory.</p>
          <p>This allows teams to:</p>
          <Bullets items={[
            "Record expected behaviour before responsibility changes hands.",
            "Expose gaps between design intent and actual operation.",
            "Track seasonal commissioning and tuning.",
            "Detect performance drift after occupation.",
            "Verify that corrective action produced a lasting result.",
          ]} />
          <Pull>Handover should transfer knowledge — not simply keys, manuals and passwords.</Pull>
        </Sec>

        <Sec eyebrow="THE DIGITAL NERVOUS SYSTEM" title="CII connects every building decision to a shared source of truth">
          <Lead>Centralised Intelligence Infrastructure brings energy, environment, assets and people into one connected operational architecture.</Lead>
          <div className="wp-diagram"><CIIOrbitDiagram /></div>
          <p>CII is the route through which information can move from physical equipment to facilities, engineering, finance, ESG and leadership. It does not replace specialist systems. It connects their evidence so the building can be understood as one environment.</p>
          <Pull>One connected source of intelligence across every asset, building and location.</Pull>
        </Sec>

        <Sec eyebrow="AI NEEDS EVIDENCE" title="Billions of daily data points strengthen the foundation for intelligent decisions">
          <Lead>At the last confirmed count supplied for this paper, the supporting technology environment was processing more than 5.3 billion data points every day — and growing.</Lead>
          <Stat big="5.3 Billion" sub="data points every day" note="Confirm the current figure and supporting methodology before external publication." />
          <p>The value is not scale alone. Continuity and context allow AI-supported analysis to recognise patterns, compare behaviour, identify abnormalities and direct human attention towards the issues that matter.</p>
          <p>For a new building, that can mean:</p>
          <Bullets items={[
            "Earlier recognition of abnormal consumption.",
            "Comparison between similar assets and operating periods.",
            "Warning of developing performance or maintenance problems.",
            "Better optimisation of generation, storage and flexible loads.",
            "A stronger evidence base for future decisions.",
          ]} />
          <p>AI is only as reliable as the information, governance and operational context beneath it. The foundation must therefore be designed with security, quality, accountability and human oversight from the outset.</p>
        </Sec>

        <Sec eyebrow="A BUILDING NEVER SLEEPS" title="Virtual Energy Management provides continuous oversight">
          <Lead>CII and the Virtual Energy Management platform can manage the energy environment 24 hours a day, seven days a week.</Lead>
          <Stat big="24 / 7 / 365" sub="continuous visibility and informed oversight" />
          <p>Out-of-hours demand, drifting schedules and developing faults do not wait for the next audit. Continuous monitoring brings the opportunity to identify change closer to the moment it begins.</p>
          <Pull>The objective is not a building full of alarms. It is a building capable of recognising what deserves attention.</Pull>
          <p>Automation, AI-supported analysis and experienced people each play a different role. Together, they allow the estate to move from retrospective investigation towards earlier, proactive management.</p>
        </Sec>

        <Sec eyebrow="ONE BUILDING. MANY DECISIONS." title="Different teams need different answers from the same evidence">
          <Lead>Granular reporting should translate technical behaviour into the language of the person responsible for acting.</Lead>
          <KV head={["Team", "Decision-ready intelligence"]} rows={[
            ["Engineering", "Asset, circuit, condition and fault behaviour"],
            ["Facilities", "Schedules, exceptions, occupancy and site comparison"],
            ["Finance", "Cost, exposure, verified outcomes and priorities"],
            ["ESG / carbon", "Traceable energy and carbon performance"],
            ["Leadership", "Risk, resilience, KPIs and strategic investment"],
          ]} />
          <p>Reporting at a different level means the right intelligence for the right person — without losing the shared source of truth.</p>
          <p>Carbon calculations at the touch of a button are powerful only when boundaries, factors, sources and version control are clear. Digital speed must be matched by reporting credibility.</p>
        </Sec>

        <Sec eyebrow="PERFORMANCE BY DESIGN" title="The building should arrive with its KPIs — not invent them after failure">
          <Lead>A well-designed KPI tells teams what normal looks like, who responds, when action is required and how improvement will be verified.</Lead>
          <p>New-build KPIs can govern:</p>
          <Bullets items={[
            "Energy intensity and significant asset performance.",
            "Out-of-hours consumption and operating schedules.",
            "Peak demand, flexibility and battery behaviour.",
            "Solar self-consumption and renewable utilisation.",
            "Indoor environmental quality and comfort.",
            "Carbon outcomes, exceptions and corrective actions.",
            "The persistence of verified improvements after handover.",
          ]} />
          <Pull>What gets connected can be seen. What gets measured can be managed. What gets verified can be protected.</Pull>
        </Sec>

        <Sec eyebrow="DESIGN FOR WHAT COMES NEXT" title="Future technologies will evolve. The foundation must be ready.">
          <Lead>A new building will encounter technologies, tariffs, regulations and operating demands that may not exist when it is designed.</Lead>
          <p>Heat pumps, EV fleets, flexible demand, new storage technologies, local energy markets and increasingly capable AI will change how buildings operate. A closed, fragmented architecture turns every evolution into another costly integration project.</p>
          <p>Future-ready design requires:</p>
          <Bullets items={[
            "Open, documented and secure interfaces.",
            "Clear ownership and portability of operational data.",
            "A common naming and asset structure.",
            "Scalable connectivity across sites and technologies.",
            "Cybersecurity and access control designed into governance.",
            "The ability to add capability without abandoning the existing foundation.",
          ]} />
          <Pull>Future-proofing does not mean predicting every technology. It means refusing to design out the ability to connect it.</Pull>
        </Sec>

        <Sec eyebrow="PROCURE THE OUTCOME" title="Specify an intelligent building — not a collection of intelligent products">
          <Lead>Individual products can all be described as smart while the completed building remains disconnected.</Lead>
          <p>The employer&rsquo;s requirements should ask bidders to demonstrate:</p>
          <Bullets items={[
            "The operational questions the design will answer.",
            "The required monitoring depth and data quality.",
            "How systems exchange information across vendor boundaries.",
            "How cybersecurity, access, ownership and retention are governed.",
            "How commissioning data becomes the operating baseline.",
            "How teams will receive role-specific reports and alerts.",
            "How performance will be verified after occupation.",
          ]} />
          <p>Compliance should be tested against connected outcomes — not the presence of isolated hardware. Open requirements allow future suppliers to improve the building without forcing the client to surrender control of its own operational intelligence.</p>
        </Sec>

        <Sec eyebrow="THE LIFECYCLE ECONOMICS" title="Prevention costs less than correction">
          <Lead>A line item removed during value engineering may reappear later as wasted energy, manual investigation, retrofit disruption and lost operational opportunity.</Lead>
          <KV head={["Design intelligence in now", "Retrofit intelligence later"]} rows={[
            ["Coordinate during design", "Survey an occupied building"],
            ["Install during construction", "Interrupt live operations"],
            ["Create a known data structure", "Reverse-engineer undocumented systems"],
            ["Baseline from commissioning", "Reconstruct history after waste occurs"],
            ["Protect interoperability", "Pay repeatedly for integration"],
          ]} />
          <Pull>The cost of intelligence should be compared with the lifetime cost of operating without it.</Pull>
          <p>Savings and payback must be established project by project from measured evidence. Avoiding preventable waste, integration cost and operational uncertainty is already a material lifecycle objective.</p>
        </Sec>

        <Sec eyebrow="BUILDING FOR PUBLIC VALUE" title="A school, hospital or council building should protect every pound it consumes">
          <Lead>Public buildings are funded to deliver education, care, safety, services and community value — not to conceal avoidable energy loss.</Lead>
          <p>When an authority creates a new school, hospital, office, leisure centre, prison or transport facility, it creates an energy commitment that may continue for generations. Designing intelligence in from the outset protects operating budgets, strengthens carbon evidence and reduces exposure to future energy pressure.</p>
          <Pull>Every pound prevented from disappearing through avoidable waste remains available for the purpose the building was created to serve.</Pull>
          <p>The same principle applies to private developments. Energy waste can be the difference between competitive and uncompetitive operating cost, between profit and loss, and — under pressure — between remaining open and closing the doors.</p>
        </Sec>

        <Sec eyebrow="FROM DESIGN INTENT TO LIVING PERFORMANCE" title="The intelligent-building pathway">
          <Lead>The foundation must survive every handoff — from client brief to design, construction, commissioning and occupation.</Lead>
          <ol className="wp-steps">
            <li><b>Define</b> — Agree the operational outcomes, risks and questions the building must answer.</li>
            <li><b>Specify</b> — Set requirements for granularity, interoperability, ownership, security and reporting.</li>
            <li><b>Connect</b> — Install sensing, communications and CII as coordinated infrastructure.</li>
            <li><b>Commission</b> — Validate data, controls, naming, access and complete-system behaviour.</li>
            <li><b>Baseline</b> — Establish normal performance with real operating context.</li>
            <li><b>Operate</b> — Apply continuous Virtual Energy Management and role-based reporting.</li>
            <li><b>Improve</b> — Use evidence, AI-supported analysis and human judgement to act and verify.</li>
          </ol>
          <Pull>Design. Connect. Baseline. Operate. Learn. Improve.</Pull>
        </Sec>

        <Sec eyebrow="QUESTIONS THAT CANNOT WAIT UNTIL HANDOVER" title="If the answer is unclear, the design is incomplete">
          <Lead>These questions turn &ldquo;smart building&rdquo; language into accountable requirements.</Lead>
          <Bullets items={[
            "What significant energy behaviour will be visible below the main meter?",
            "Can every critical system exchange useful information with the wider building?",
            "Who owns the data, the baseline and the response to deviation?",
            "Will commissioning evidence remain usable after the project team leaves?",
            "Can solar, batteries, EV charging and future technologies be integrated and optimised?",
            "Can engineering, finance, ESG and leadership each obtain decision-ready reporting?",
            "How will improvement be verified — and how will drift be detected?",
            "What prevents the building from becoming dependent on one closed supplier?",
          ]} />
          <Pull>If the building cannot explain its behaviour, it cannot govern its performance.</Pull>
        </Sec>

        <Sec eyebrow="THE STANDARD FOR TOMORROW" title="Every new building should be born intelligent">
          <Lead>We now have the hardware, software, data and intelligence to prevent waste at its source — before it becomes a permanent operating cost.</Lead>
          <p>A new building should understand its energy behaviour at a granular level. It should establish its truth through a living baseline. It should connect every critical technology through CII. It should use AI-supported analysis and continuous Virtual Energy Management to recognise change earlier. It should report credible intelligence to every team responsible for performance.</p>
          <Pull>All buildings will think for themselves.</Pull>
          <p>The question is whether we design that capability deliberately — or pay to reconstruct it after years of invisible loss.</p>
          <p className="wp-warn">Do not build tomorrow&rsquo;s buildings with yesterday&rsquo;s old energy methods.</p>
        </Sec>

        <footer className="wp-end">
          <img className="wp-logo" src="/brand/logo.png" alt="Energy Vault Group" />
          <div className="wp-end-title">Born <span className="g">Intelligent</span></div>
          <div className="wp-end-sub">STOP WASTE BEFORE IT BEGINS</div>
          <div className="wp-end-meta">Energy Vault Group&nbsp;&nbsp;|&nbsp;&nbsp;For Council New Builds&nbsp;&nbsp;|&nbsp;&nbsp;Strategic discussion edition</div>
          <a href="/gap-analysis" className="btn no-print wp-end-cta" data-testid="wp-cta">Book Your Free Gap Analysis</a>
        </footer>
      </article>
    </div>
  );
}
